import React, { type ReactNode } from 'react';
import cx from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import { pageHeaderRowClass, pageTitleClass, tabClass } from '@app/constants/styles';
import { TabBar } from './tab-bar';
import { useViewTransitionNav } from './viewTransition';

export interface SectionTab {
  label: string;
  to: string;
}

/**
 * Header for a section that merges two peer views behind one sidebar item — a per-sprint "Reporting"
 * list and an entity list (Employees / Products). Renders a small section title with an optional
 * right-aligned action (sprint toggle, "Add …" button) and a tab bar linking the peer routes. The
 * active tab is derived from the URL, so refresh and deep links keep working. Mirrors the
 * product-detail tab bar for visual consistency.
 */
export const SectionTabsHeader = ({
  title,
  tabs,
  action,
  className,
}: {
  title: ReactNode;
  tabs: SectionTab[];
  action?: ReactNode;
  className?: string;
}): React.ReactElement => {
  const { pathname } = useLocation();
  const transitionNavigate = useViewTransitionNav();
  // `startsWith(to + '/')` matches detail routes without letting a sibling like `/members` light up
  // on `/members-reporting` (the next char there is `-`, not `/`).
  const isActive = (to: string): boolean => pathname === to || pathname.startsWith(`${to}/`);

  // These two views are peers behind one sidebar item, so switching cross-fades the page — the same quick
  // beat as the other same-level switches (see the `fade` rules in globals.css), and no swipe, since you're
  // not going a layer deeper. Modified and middle clicks fall through so "open in new tab" still works.
  const switchTab = (e: React.MouseEvent, to: string, active: boolean): void => {
    if (active || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    transitionNavigate(to, 'fade');
  };

  return (
    // mb-5 below the header cluster, mt-3.5 from the title to the tab strip: the page-title rhythm every
    // page follows (see Insights, which renders the same two rows by hand).
    <div className={cx('mb-5', className)}>
      <div className={cx(pageHeaderRowClass, 'flex flex-wrap items-center justify-between gap-4')}>
        <h1 className={pageTitleClass}>{title}</h1>
        {action}
      </div>
      <TabBar wrapperClassName="mt-3.5" activeKey={pathname}>
        {tabs.map(tab => {
          const active = isActive(tab.to);
          return (
            <Link
              key={tab.to}
              to={tab.to}
              onClick={e => switchTab(e, tab.to, active)}
              aria-current={active ? 'page' : undefined}
              className={tabClass(active)}
            >
              {tab.label}
            </Link>
          );
        })}
      </TabBar>
    </div>
  );
};
